import { FormField } from '@/modules/forms/formConfig';
import { FormsAnswerReview_AnswerFragment, FormsAnswerReview_RevisionFragment } from '@/modules/forms/graphql/FormSubmissions.generated';
import { AnswerRevisionOrigin } from '@/types/schema';
import { AnswerReviewCurrentRevision, AnswerReviewEvidence, AnswerReviewValue } from './AnswerReviewWorkbench.types';
import { formatEvidenceProvision } from './formatEvidenceProvision';
import { FormSubmissionAdapterError, parseAnswerValue } from './formSubmissionFields';
type Answer = FormsAnswerReview_AnswerFragment;
type Field = FormField;
type Revision = FormsAnswerReview_RevisionFragment;
type MappedAnswer = {
  currentRevision: AnswerReviewCurrentRevision | null;
  reasoning?: string;
  evidence: AnswerReviewEvidence[];
};
function assertScalarRevision(revision: Revision): void {
  if (revision.answerTree != null || revision.evidence?.some(item => item.target != null)) throw new FormSubmissionAdapterError('A scalar revision must not contain typed answer data.');
}
export function mapEvidence(evidence: Revision['evidence'], revisionID: string): AnswerReviewEvidence[] {
  return (evidence ?? []).flatMap(({
    document,
    fileParagraphs
  }, index) => {
    const paragraphs = fileParagraphs ?? [];
    if (!paragraphs.length) throw new FormSubmissionAdapterError(`Evidence ${index + 1} for revision "${revisionID}" has no paragraphs.`);
    // A carousel item contains one paragraph and only that paragraph's highlights.
    return paragraphs.map(paragraph => {
      const annotations = paragraph.boundingRegions.map(region => ({
        ...region,
        documentID: document.id,
        documentName: document.name
      }));
      const pages = Array.from(new Set(annotations.map(item => item.PageNumber)));
      return {
        id: `reference:${paragraph.id}`,
        documentId: document.id,
        documentName: document.name,
        referenceLabel: formatEvidenceProvision(paragraph.ocrStructure),
        paragraphIds: [paragraph.id],
        pageLabel: pages.length ? `Page${pages.length === 1 ? '' : 's'} ${pages.join(', ')}` : undefined,
        quote: paragraph.content,
        annotations
      };
    });
  });
}
function parseAIProvenance(revision: Revision, field: Field): MappedAnswer {
  assertScalarRevision(revision);
  const confidence = revision.confidence;
  if (!confidence) throw new FormSubmissionAdapterError(`AI answer for field "${field.id}" has no confidence.`);
  const value = parseAnswerValue(revision.value, field, confidence === 'NOT_FOUND');
  const evidence = mapEvidence(revision.evidence, revision.id);
  if (confidence === 'NOT_FOUND') {
    const isBlank = value === null || value === '' || Array.isArray(value) && !value.length;
    if (!isBlank || evidence.length) throw new FormSubmissionAdapterError(`NOT_FOUND answer for field "${field.id}" must have a blank value and no evidence.`);
    // The review uses null for blanks; the saved revision keeps its typed value.
    return {
      currentRevision: {
        origin: 'AI',
        confidence: 'NOT_FOUND',
        value: null
      },
      reasoning: revision.reasoning ?? undefined,
      evidence
    };
  }
  if (value === null || !evidence.length) throw new FormSubmissionAdapterError(`${confidence} answer for field "${field.id}" must have a value and evidence.`);
  return {
    currentRevision: {
      origin: 'AI',
      confidence,
      value
    },
    reasoning: revision.reasoning ?? undefined,
    evidence
  };
}
export function mapAnswer(answer: Answer | null | undefined, field: Field, draftValue?: AnswerReviewValue): MappedAnswer {
  const revision = answer?.currentRevision;
  if (!revision) {
    return {
      currentRevision: draftValue === undefined ? null : {
        origin: 'USER',
        confidence: null,
        value: draftValue
      },
      evidence: []
    };
  }
  if (answer.fieldID !== field.id) throw new FormSubmissionAdapterError(`Answer field "${answer.fieldID}" does not match field "${field.id}".`);
  assertScalarRevision(revision);
  if (revision.origin === AnswerRevisionOrigin.Ai) {
    const ai = parseAIProvenance(revision, field);
    return {
      ...ai,
      currentRevision: draftValue === undefined ? ai.currentRevision : {
        origin: 'USER',
        confidence: null,
        value: draftValue
      }
    };
  }
  if (revision.origin !== AnswerRevisionOrigin.User) throw new FormSubmissionAdapterError(`Field "${field.id}" has an unknown answer origin.`);
  if (revision.confidence != null) throw new FormSubmissionAdapterError(`USER answer for field "${field.id}" has confidence.`);
  const currentValue = parseAnswerValue(revision.value, field, true);
  const provenance = answer.latestAIRevision ? parseAIProvenance(answer.latestAIRevision, field) : undefined;
  return {
    currentRevision: {
      origin: 'USER',
      confidence: null,
      value: draftValue === undefined ? currentValue : draftValue
    },
    reasoning: provenance?.reasoning,
    // Only the selected revision supplies references; AI history is provenance.
    evidence: mapEvidence(revision.evidence, revision.id)
  };
}